import {
  useEffect,
  useRef,
  useState,
  Fragment,
  useLayoutEffect,
  useMemo,
} from 'react';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import moment from 'moment';
import { BsInfoCircle, BsThreeDots } from 'react-icons/bs';
import { IoMdCheckmarkCircleOutline, IoMdReturnRight } from 'react-icons/io';
import M from 'materialize-css';
import ContextMenu from '../../components/ContextMenu.tsx';
import DataMatrixInput from '../../components/form/DataMatrixInput.tsx';
import queryHooks from '../../common/queryHooks/index.ts';
import mutationHooks from '../../common/mutationHooks/index.ts';
import QuarantineMedModal from '../quarantineMeds/QuarantineMedModal.tsx';
import { toast } from '../../func.ts';
import useCurrentCustomerLocationId from '../../hooks/useCurrentCustomerLocation.ts';

const OrderScan = () => {
  const { documentId } = useParams();
  const { customerLocationId } = useCurrentCustomerLocationId();
  const medGroupTriggers = useRef([]);

  const ref = useRef({
    selectedMedVocabularyId: 0,
    totalMeds: 0,
  });

  const tooltip = useRef();

  const [tableData, setTableData] = useState([]);
  const [selectedMed, setSelectedMed] = useState(null);
  const [quarantineModalIsOpen, setQuarantineModalIsOpen] = useState(false);
  const [quarantineMedData, setQuarantineMedData] = useState(null);
  const [scanData, setScanData] = useState(null);
  const [note, setNote] = useState('');

  const { data: meds = [], isLoading: isLoadingMeds } =
    queryHooks.order.useReadAllMedsByDocumentId(documentId);

  const focusInput = () => {
    const sgtinElement = document.querySelector('#receive-med-barcode-scanner');
    setTimeout(() => {
      sgtinElement.select();
    }, 600);
  };

  const {
    mutate: receiveMedsBySerialNumber,
    isPending: isPendingReceiveMedsBySerialNumber,
  } = mutationHooks.med.useUpdateReceiveManyBySerialNumber({
    onMutate: focusInput,
    onSuccess: data => {
      if (data?.payload?.msg?.includes('Med Not Found: ')) {
        if (!scanData || !documentId) {
          return toast('Missing Data: Could not quarantine med');
        } else if (scanData && documentId) {
          setQuarantineModalIsOpen(true);
          return setQuarantineMedData({
            ...scanData,
            documentId,
            customerLocationId: customerLocationId || 0,
            note,
          });
        }
      }
    },
  });

  const {
    mutate: receiveMedsByEpcisMedVocabularyId,
    isPending: isPendingReceiveManyByEpcisMedVocabularyId,
  } = mutationHooks.med.useUpdateReceiveManyByEpcisMedVocabularyId({
    onMutate: focusInput,
  });

  const {
    mutate: receiveMedsByDocumentId,
    isPending: isPendingRecieveMedsByDocumentId,
  } = mutationHooks.med.useUpdateReceiveAllByDocumentId({
    onMutate: focusInput,
  });

  const {
    mutate: receiveMedsByShippingContainerCode,
    isPending: isPendingReceiveMedsByShippingContainerCode,
  } = mutationHooks.med.useUpdateReceiveManyByShippingContainerCode({
    onMutate: focusInput,
  });

  const {
    mutate: unverifyReceiptBySgtinId,
    isPending: isPendingUnverifyReceiptBySgtinId,
  } = mutationHooks.med.useUpdateOneUnverifyReceipt();

  const noteOnChange = ({ target: { value } }) => setNote(value);

  useLayoutEffect(() => {
    M.Modal.init(document.querySelector('#med-detail-modal'));
    document.querySelector('#receive-med-barcode-scanner')?.focus();
    return () =>
      document.querySelectorAll('.modal').forEach(el => {
        if (el) {
          M.Modal.getInstance(el)?.destroy();
        }
      });
  }, []);

  useEffect(() => {
    const tableData = [];
    const drillDown = (arr, hierarchy = []) => {
      arr.forEach(item => {
        const newhierarchy = [...hierarchy]; // Create a copy of the hierarchy array
        if (item.children) {
          const hierarchyData = JSON.parse(JSON.stringify(item));
          delete hierarchyData.children;

          newhierarchy.push(hierarchyData); // Push to the new array
          drillDown(item.children, newhierarchy); // Pass the new array to the recursive call
        } else {
          const lotNumber =
            item.epcisEvents.filter(event => event.lotNumber)[0]?.lotNumber ??
            '';
          const group = tableData.find(
            group =>
              group.epcisMedVocabularyId === item.epcisMedVocabularyId &&
              group.lotNumber === lotNumber
          );

          if (group) group.meds.push(item);
          else {
            const lotNumber =
              item.epcisEvents.filter(event => event.lotNumber)[0]?.lotNumber ??
              '';

            tableData.push({
              epcisMedVocabularyId: item.epcisMedVocabularyId,
              lotNumber,
              hierarchy: newhierarchy, // Use the new array here
              meds: [item],
            });
          }
        }
      });
    };

    if (meds) {
      drillDown(meds);
      setTableData(tableData);
    }
  }, [meds]);

  const Table = useMemo(
    () => styled.table`
      tr:has(+ tr.table-drawer[data-open='open']) {
        border: 2px solid black;
      }

      tr.table-drawer {
        display: none;

        &[data-open='open'] {
          display: table-row;
          border-bottom: 2px solid black;

          & > td {
            border-left: 2px solid black;
            border-right: 2px solid black;
          }
        }
      }
    `,
    []
  );

  const toggleTableDrawer = (event, id) => {
    const td = document.querySelector(`#table-top-level-context-${id}`);
    if (td.contains(event.target)) {
      event.stopPropagation();
      ref.current.selectedMedVocabularyId = id;
      return;
    }

    const drawer = document.getElementById(`table-drawer-${id}`);

    if (drawer) {
      const status = drawer.getAttribute('data-open');
      drawer.setAttribute('data-open', status === 'open' ? 'closed' : 'open');
    }
  };

  const showModal = (med, hierarchy) => {
    setSelectedMed({ ...med, hierarchy });
    const el = document.querySelector('#med-detail-modal');
    const modal = M.Modal.getInstance(el);
    modal.open();
  };

  const receiveEntireOrder = async event => {
    event?.preventDefault();
    if (
      window.confirm(
        'Are you sure you would like to mark every item on this order as verified?'
      )
    ) {
      receiveMedsByDocumentId(documentId);
    }
  };
  const onScan = data => {
    const { unknown } = data;
    const serialNumber = data[`SERIAL NUMBER`];
    const sscc = data[`SERIAL SHIPPING CONTAINER CODE (SSCC)`];

    if (serialNumber) {
      const medData = {
        lotNumber: data['BATCH/LOT NUMBER'] || '',
        expirationDate: data['EXPIRATION DATE (YYMMDD)'] || '',
        gtin: data['GLOBAL TRADE ITEM NUMBER (GTIN)'] || '',
        serialNumber: data['SERIAL NUMBER'] || '',
      };

      setScanData(medData);

      return receiveMedsBySerialNumber({
        serialNumber,
        documentIds: [documentId],
      });
    }

    if (sscc)
      return receiveMedsByShippingContainerCode({
        shippingContainerCode: sscc,
        documentId: documentId,
      });
    if (unknown)
      return receiveMedsBySerialNumber({
        serialNumber: unknown.replaceAll(/\D/g, ''),
        documentIds: [documentId],
      });
  };

  const isMutating = useMemo(
    () =>
      isPendingReceiveMedsBySerialNumber ||
      isPendingReceiveManyByEpcisMedVocabularyId ||
      isPendingReceiveMedsByShippingContainerCode ||
      isPendingRecieveMedsByDocumentId ||
      isPendingUnverifyReceiptBySgtinId,
    [
      isPendingReceiveMedsBySerialNumber,
      isPendingReceiveManyByEpcisMedVocabularyId,
      isPendingReceiveMedsByShippingContainerCode,
      isPendingRecieveMedsByDocumentId,
      isPendingUnverifyReceiptBySgtinId,
    ]
  );

  const unverifyReceipt = (event, med) => {
    event?.preventDefault();

    if (
      window.confirm(
        `Are you sure you want to unverify the receipt of med: ${med.sgtin.split('.').pop()}?`
      )
    )
      unverifyReceiptBySgtinId({
        epcisSgtinId: med.id,
        documentId,
      });
  };

  const countMeds = item => {
    if (Array.isArray(item)) return item.forEach(child => countMeds(child));
    const { children, epcisMedVocabulary } = item;
    if (
      epcisMedVocabulary?.regulatedProductName?.toLocaleLowerCase() !==
      'unspecified container'
    )
      ref.current.totalMeds += 1;
    if (children) return children.forEach(child => countMeds(child));
  };

  useEffect(() => {
    if (!meds) return;
    ref.current.totalMeds = 0;
    countMeds(meds);
  }, [meds]);

  useEffect(() => {
    const { current } = tooltip;
    if (!current) return;

    M.Tooltip.init(current, {
      html: `<span>This figure includes all of the individual serialized items<br />
                    as well as any packaging items with serial numbers.<br /> 
                    For example, if you received a package of 10 bottles of aspirin<br />
                    it would show 11 total items: 1 for the package,<br />
                    10 for the bottles.</span>`,
    });
    return () => M.Tooltip.getInstance(current)?.destroy();
  }, [meds, isLoadingMeds]);

  return (
    <div>
      <div className="row">
        <div className="input-field col s8 m4">
          <DataMatrixInput
            id="receive-med-barcode-scanner"
            onScan={onScan}
            allowPassiveInput={true}
            isLoading={isMutating}
          />
          <label htmlFor="receive-med-barcode-scanner">Scan Med or Tote</label>
        </div>
        <div className="input-field col s4 m2 offset-m6">
          <a href="/" onClick={receiveEntireOrder}>
            Verify All
          </a>
        </div>
      </div>
      {isLoadingMeds ? (
        <div className="row">
          <div className="progress blue">
            <div className="indeterminate" />
          </div>
        </div>
      ) : (
        <div className="row">
          <Table>
            <thead>
              <tr>
                <th style={{ padding: '4px' }}>Name</th>
                <th style={{ padding: '4px' }}>Manufacturer</th>
                <th style={{ padding: '4px' }}>NDC</th>
                <th style={{ padding: '4px' }}>Lot #</th>
                <th style={{ padding: '4px' }}>Expiration</th>
                <th style={{ padding: '4px' }}>Type</th>
                <th style={{ padding: '4px' }}>Description</th>
                <th style={{ padding: '4px', textAlign: 'right' }}>Verified</th>
                <th style={{ padding: '4px', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '4px' }}></th>
              </tr>
            </thead>
            <tbody>
              {tableData?.map((medGroup, index) => {
                const med = medGroup.meds[0];
                const receivedCount = medGroup.meds?.filter(
                  med => med.receivedById
                ).length;
                const totalCount = medGroup.meds?.length;
                const isReceived = receivedCount === totalCount;
                const {
                  meds: [
                    {
                      epcisMedVocabulary: {
                        id: epcisMedVocabularyId,
                        manufacturerOfTradeItemPartyName: manufacturerName,
                      },
                      epcisEvents: events,
                    },
                  ],
                } = medGroup;

                const lotNumber = events.find(({ lotNumber }) =>
                  Boolean(lotNumber)
                )?.lotNumber;
                const expiration = events.find(({ expiration }) =>
                  Boolean(expiration)
                )?.expiration;

                // replace '.' to create valid selector
                const drawerId = `${epcisMedVocabularyId}-${lotNumber.replaceAll('.', '') ?? ''}`;

                return (
                  <Fragment key={drawerId}>
                    <tr
                      style={{ cursor: 'pointer' }}
                      className={`bold`}
                      onClick={event => toggleTableDrawer(event, drawerId)}
                    >
                      <td style={{ padding: '8px', borderRadius: '0px' }}>
                        {med?.epcisMedVocabulary?.regulatedProductName}
                      </td>
                      <td style={{ padding: '8px', borderRadius: '0px' }}>
                        {manufacturerName ?? ''}
                      </td>
                      <td style={{ padding: '8px', borderRadius: '0px' }}>
                        {med?.epcisMedVocabulary?.additionalTradeItemId}
                      </td>
                      <td style={{ padding: '8px', borderRadius: '0px' }}>
                        {lotNumber ?? ''}
                      </td>
                      <td style={{ padding: '8px', borderRadius: '0px' }}>
                        {expiration ?? ''}
                      </td>
                      <td style={{ padding: '8px', borderRadius: '0px' }}>
                        {med?.epcisMedVocabulary?.dosageFormType === 'Unknown'
                          ? ''
                          : med?.epcisMedVocabulary?.dosageFormType || ''}
                      </td>
                      <td style={{ padding: '8px', borderRadius: '0px' }}>
                        {med?.epcisMedVocabulary?.strengthDescription}
                      </td>
                      <td
                        style={{
                          padding: '0px',
                          borderRadius: '0px',
                          textAlign: 'right',
                        }}
                      >
                        {isReceived ? (
                          <IoMdCheckmarkCircleOutline
                            className="green-text"
                            style={{
                              fontSize: '24px',
                              position: 'relative',
                              top: '3px',
                              right: '12px',
                            }}
                          />
                        ) : (
                          receivedCount
                        )}
                      </td>
                      <td
                        style={{
                          padding: '8px',
                          borderRadius: '0px',
                          textAlign: 'right',
                        }}
                      >
                        {totalCount}
                      </td>
                      <td ref={el => (medGroupTriggers.current[index] = el)}>
                        <BsThreeDots
                          id={`table-top-level-context-${drawerId}`}
                          className="right"
                        />
                      </td>
                    </tr>
                    <tr
                      id={`table-drawer-${drawerId}`}
                      className="table-drawer"
                      data-open="closed"
                    >
                      <td
                        style={{ padding: '2px', borderBottom: 'none' }}
                        colSpan={6}
                      >
                        <table style={{ margin: '0px', padding: '0px' }}>
                          <thead>
                            <tr>
                              <th style={{ padding: '0px' }}></th>
                              <th style={{ padding: '0px' }}>Serial #</th>
                              <th style={{ padding: '0px' }}>Date Verified</th>
                              <th style={{ padding: '0px' }}>Verified By</th>
                              <th style={{ padding: '0px' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {medGroup.meds?.map((med, index) => (
                              <tr
                                key={med.sgtin}
                                style={
                                  index === medGroup.meds.length - 1
                                    ? { border: 'none' }
                                    : {}
                                }
                              >
                                <td style={{ padding: '2px' }}></td>
                                <td style={{ padding: '2px' }}>
                                  {med.sgtin.split('.').pop()}
                                </td>
                                <td style={{ padding: '2px' }}>
                                  {med.datetimeReceived
                                    ? moment(med.datetimeReceived).format(
                                        'MM/DD/YY'
                                      )
                                    : ''}
                                </td>
                                <td style={{ padding: '2px' }}>
                                  {med.receivedById
                                    ? `${med.receivedBy.firstName} ${med.receivedBy.lastName}`
                                    : ''}
                                </td>
                                <td style={{ padding: '2px' }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'end',
                                      gap: '10%',
                                    }}
                                  >
                                    {!med.receivedById ? (
                                      <a
                                        href="/"
                                        onClick={event => {
                                          event.preventDefault();
                                          const serialNumber = med.sgtin
                                            .split('.')
                                            .pop();
                                          ref.current.sgtin = serialNumber;
                                          receiveMedsBySerialNumber({
                                            serialNumber,
                                            documentIds: [documentId],
                                          });
                                        }}
                                      >
                                        Verify Med
                                      </a>
                                    ) : (
                                      <a
                                        href="/"
                                        onClick={e => unverifyReceipt(e, med)}
                                      >
                                        Unverify
                                      </a>
                                    )}
                                    <a
                                      href="/"
                                      onClick={event => {
                                        event.preventDefault();
                                        showModal(med, medGroup.hierarchy);
                                      }}
                                    >
                                      View Detail
                                    </a>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
          <div className="row">
            {meds && (
              <div
                className="right"
                style={{
                  marginTop: '10px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'end',
                  minWidth: '180px',
                  gap: '6px',
                }}
              >
                <span className="grey-text">
                  Total Items: {ref.current.totalMeds}
                </span>
                <a
                  ref={tooltip}
                  className="tooltipped"
                  href="/"
                  onClick={e => e.preventDefault()}
                  data-position="bottom"
                >
                  <BsInfoCircle
                    className="grey-text"
                    style={{ fontSize: '14px' }}
                  />
                </a>
              </div>
            )}
          </div>
        </div>
      )}
      <div id="med-detail-modal" className="modal">
        {selectedMed ? (
          <div className="modal-content">
            <div className="row">
              <h5 className="bolder">
                <i>{selectedMed.sgtin}</i>
              </h5>
            </div>
            <div className="divider" />
            <div
              className="row"
              style={{
                fontSize: '1.25rem',
                padding: '2rem',
                marginBottom: '60px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'start',
                  alignItems: 'start',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'start',
                    gap: '12px',
                  }}
                >
                  <b>Med Name:</b>
                  <span>{`${
                    selectedMed.epcisMedVocabulary.regulatedProductName
                  } ${
                    selectedMed.epcisMedVocabulary.strengthDescription
                      ? ` - ${selectedMed.epcisMedVocabulary.strengthDescription}`
                      : ''
                  }`}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'start',
                    gap: '12px',
                  }}
                >
                  <b>Med Serial Prefix:</b>
                  <span>
                    {selectedMed.epcisMedVocabulary.sgtin.replace('.*', '')}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'start',
                    gap: '12px',
                  }}
                >
                  <b>Item Serial Number:</b>
                  <span>
                    {selectedMed.sgtin
                      .replace(
                        selectedMed.epcisMedVocabulary.sgtin.replace('*', ''),
                        ''
                      )
                      .replace('.', '')}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'start',
                    gap: '12px',
                  }}
                >
                  <b>NDC:</b>
                  <span>
                    {selectedMed.epcisMedVocabulary.additionalTradeItemId || ''}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'start',
                    gap: '12px',
                  }}
                >
                  <b>Dosage Form Type:</b>
                  <span>
                    {selectedMed.epcisMedVocabulary.dosageFormType === 'Unknown'
                      ? ''
                      : selectedMed.epcisMedVocabulary.dosageFormType || ''}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'start',
                    gap: '12px',
                  }}
                >
                  <b>Date Verified:</b>
                  <span>
                    {selectedMed.datetimeReceived
                      ? moment(selectedMed.datetimeReceived).format('MM/DD/YY')
                      : ''}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'start',
                    gap: '12px',
                  }}
                >
                  <b>Verified By:</b>
                  <span>
                    {selectedMed.receivedById
                      ? selectedMed.receivedBy.firstName +
                        ' ' +
                        selectedMed.receivedBy.lastName
                      : ''}
                  </span>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="row" style={{ marginBottom: '5px' }}>
                <h5 className="bold">Packaging Detail</h5>
              </div>
              <div className="divider" style={{ marginBottom: '15px' }} />
              <div
                className="row"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'start',
                  alignContent: 'start',
                  fontSize: '1.1em',
                }}
              >
                {selectedMed.hierarchy && selectedMed.hierarchy.length ? (
                  <>
                    {selectedMed.hierarchy?.map((parentMed, index) => (
                      <div
                        key={parentMed.sgtin}
                        style={{
                          paddingLeft: 2 * index + 'rem',
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'start',
                          gap: '12px',
                        }}
                      >
                        {index !== 0 ? <IoMdReturnRight /> : null}
                        <span className="bold">
                          {parentMed.epcisMedVocabulary.regulatedProductName} -{' '}
                          {parentMed.sgtin}
                        </span>
                      </div>
                    ))}
                    <div
                      style={{
                        paddingLeft: 2 * selectedMed.hierarchy.length + 'rem',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'start',
                        gap: '12px',
                      }}
                    >
                      <IoMdReturnRight />
                      <span className="bold">
                        <i>This Med</i>
                      </span>
                    </div>
                  </>
                ) : (
                  <p>This med was not part of a larger package</p>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <ContextMenu
        triggerRef={medGroupTriggers}
        options={{
          'Verify All': () =>
            receiveMedsByEpcisMedVocabularyId({
              epcisMedVocabularyId: ref.current.selectedMedVocabularyId,
              documentId,
            }),
        }}
      />
      <QuarantineMedModal
        isOpen={quarantineModalIsOpen}
        setIsOpen={setQuarantineModalIsOpen}
        quarantineMedData={quarantineMedData}
        note={note}
        setNote={setNote}
        onChange={noteOnChange}
      />
    </div>
  );
};

export default OrderScan;
